/**
 * URL Builder utility for constructing API endpoints
 * Builds proper photo stream URLs using actual photo IDs from env-configured API base URL
 */

/**
 * Get the API base URL from environment
 */
export const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:4500/api';
};

/**
 * Build photo stream URL for original image
 * @param photoId - Photo ID (not placeholder like {photoId})
 * @returns Full URL to stream endpoint
 * @example
 * getPhotoStreamUrl(42) → 'http://localhost:4500/api/photos/42/stream'
 */
export const getPhotoStreamUrl = (photoId: number): string => {
  const apiBaseUrl = getApiBaseUrl();
  return `${apiBaseUrl}/photos/${photoId}/stream`;
};

/**
 * Build photo stream URL for thumbnail
 * @param photoId - Photo ID (not placeholder like {photoId})
 * @returns Full URL to stream endpoint with thumb query param
 * @example
 * getPhotoThumbnailUrl(42) → 'http://localhost:4500/api/photos/42/stream?thumb=true'
 */
export const getPhotoThumbnailUrl = (photoId: number): string => {
  const apiBaseUrl = getApiBaseUrl();
  return `${apiBaseUrl}/photos/${photoId}/stream?thumb=true`;
};

/**
 * Build the public (unauthenticated) frontend page URL for a share link
 * @param token - Share link token
 * @example
 * getSharePageUrl('abc123') → 'http://localhost:5173/share/abc123'
 */
export const getSharePageUrl = (token: string): string => {
  return `${window.location.origin}/share/${token}`;
};

/**
 * Build the public (unauthenticated) backend image URL for a share link
 * @param token - Share link token
 * @example
 * getShareImageUrl('abc123') → 'http://localhost:4500/api/share/abc123/image'
 */
export const getShareImageUrl = (token: string): string => {
  const apiBaseUrl = getApiBaseUrl();
  return `${apiBaseUrl}/share/${token}/image`;
};
